import React from 'react';

import { useIntl } from 'react-intl';

import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import { messages } from '@atlaskit/editor-common/lists';
import { useEditorToolbar } from '@atlaskit/editor-common/toolbar';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { MoreItemsIcon, ToolbarDropdownMenu, ToolbarTooltip } from '@atlaskit/editor-toolbar';

import type { ToolbarListsIndentationPlugin } from '../../toolbarListsIndentationPluginType';
import { useIndentationState } from '../utils/hooks';
import { isMarkdownCompatibleToolbarEnabled } from '../utils/markdown-compatible-toolbar';

type ListsIndentationMenuProps = {
	allowHeadingAndParagraphIndentation: boolean;
	api?: ExtractInjectionAPI<ToolbarListsIndentationPlugin>;
	children: React.ReactNode;
};

export const ListsIndentationMenu = ({
	children,
	api,
	allowHeadingAndParagraphIndentation,
}: ListsIndentationMenuProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { editorView } = useEditorToolbar();

	const indentationState = useIndentationState({
		api,
		allowHeadingAndParagraphIndentation,
		state: editorView?.state,
	});
	const isMarkdownToolbarEnabled = isMarkdownCompatibleToolbarEnabled();
	const { bulletListDisabled, orderedListDisabled, taskListActive, markdownView } =
		useSharedPluginStateWithSelector(api, ['list', 'taskDecision', 'markdownMode'], (states) => ({
			bulletListDisabled: states.listState?.bulletListDisabled,
			orderedListDisabled: states.listState?.orderedListDisabled,
			taskListActive: states.taskDecisionState?.isInsideTask,
			markdownView: isMarkdownToolbarEnabled ? states.markdownModeState?.view : undefined,
		}));
	const isInSourceView = isMarkdownToolbarEnabled && markdownView === 'syntax';

	const allItemsDisabled =
		!isInSourceView &&
		bulletListDisabled &&
		orderedListDisabled &&
		indentationState?.indentDisabled &&
		indentationState?.outdentDisabled &&
		!taskListActive;

	return (
		<ToolbarDropdownMenu
			iconBefore={<MoreItemsIcon label={formatMessage(messages.lists)} />}
			isDisabled={allItemsDisabled}
			testId="editor-toolbar__lists-and-indentation-menu"
			label={formatMessage(messages.lists)}
			tooltipComponent={<ToolbarTooltip content={formatMessage(messages.lists)} />}
		>
			{children}
		</ToolbarDropdownMenu>
	);
};
